//Global imports
import {
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CForm,
    CFormCheck,
    CFormInput,
    CFormLabel,
    CFormSwitch,
    CFormTextarea,
} from '@coreui/react';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

//File imports
import { APP_LANGAUGE, LANGAUGE_NAME, WEBSITE_BASE_URL } from '../../Constant';
import { Language } from '../../language/English';
import Notification from '../../services/Notification/Notification';
import { DocumentCategoryPageTranslationService } from './Api/DocumentCategoryPageTranslationService';
import Logger from '../../services/Logger/Logger';
import { DocumentCategoryPageTranslationPojo } from './Api/DocumentCategoryPageTranslationPojo';
import {
    DOCUMENT_CATEGORY_PAGE_TRANSLATION_STATUS_ACTIVE,
    DOCUMENT_CATEGORY_PAGE_TRANSLATION_STATUS_INACTIVE,
} from './Api/DocumentCategoryPageTranslationConstants';
import DocumentCategoryTranslationHeadingHint from '../../assets/images/document_category_translation_heading_hint.webp';
import DocumentCategoryTranslationDescriptionHint from '../../assets/images/document_category_translation_description_hint.webp';
import DocumentCategoryTranslationTitleHint from '../../assets/images/document_category_translation_title_hint.webp';
import StringUtils from '../../services/StringUtils/StringUtils';

//Component imports
import Loader from '../../components/Layout/Loader';
import { Layout } from '../../components/Layout';
import {
    FieldHeadingWithHintText,
    FieldPreviewModal,
    HintText,
    LengthChecker,
} from '../../components';
import { SeoFields } from '../DocumentTemplateTranslation/Components';

interface FormErrors {
    url?: string;
    metaTitle?: string;
    metaKeyword?: string;
    metaDescription?: string;
    title?: string;
    heading?: string;
    description?: string;
    [key: string]: any;
}

const DocumentCategoryTranslation = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const id = location.state?.id ?? parseInt(params.get('id') ?? '0');
    const language = location.state?.language ?? params.get('language');
    const isEdit = location.state?.isEdit ?? params.get('isEdit') === 'true';
    const name = location.state?.name ?? params.get('name');
    const notification = new Notification();
    const stringUtils = new StringUtils();
    const documentCategoryPageTranslationService =
        new DocumentCategoryPageTranslationService();
    const logger = new Logger();

    const [formData, setFormData] = useState<any>({
        documentCategoryPageTranslationId: '',
        documentCategoryPageId: '',
        url: '',
        metaTitle: '',
        metaKeyword: '',
        metaDescription: '',
        title: '',
        heading: '',
        description: '',
        status: '',
        documentTemplateIds: [],
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [translationDataLoading, setTranslationDataLoading] =
        useState<boolean>(false);
    const [translationData, setTranslationData] =
        useState<DocumentCategoryPageTranslationPojo | null>(null);
    const [isEditDocument, setIsEditDocument] = useState<boolean>(false);
    const [fieldPreviewModalVisible, setFieldPreviewModalVisible] =
        useState(false);
    const [hintImage, setHintImage] = useState<string>('');
    const [allActiveDocumentTemplates, setAllActiveDocumentTemplates] =
        useState<{ documentTemplateId: number; name: string }[]>([]);
    const [searchDocumentTemplateText, setSearchDocumentTemplateText] =
        useState<string>('');

    useEffect(() => {
        getDocumentTemplates();
    }, []);

    useEffect(() => {
        if (isEdit) {
            setTranslationDataLoading(true);
            getDocumentGroupPageTranslationData();
        }
    }, [isEdit]);

    const getDocumentTemplates = async () => {
        try {
            const { success, message, data } =
                await documentCategoryPageTranslationService.getAllActiveDocumentTemplates();
            if (success) {
                setAllActiveDocumentTemplates(data);
            } else {
                notification.error(
                    message ?? Language.UNABLE_TO_FETCH_TRANSLATION_DATA,
                );
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setTranslationDataLoading(false);
        }
    };

    const getDocumentGroupPageTranslationData = async () => {
        try {
            let payload = {
                documentCategoryPageId: id,
                languageId: APP_LANGAUGE[language],
            };
            const { success, message, data } =
                await documentCategoryPageTranslationService.get(payload);
            const {
                documentCategoryPageTranslationId,
                documentCategoryPageId,
                url,
                metaTitle,
                metaKeyword,
                metaDescription,
                title,
                heading,
                description,
                status,
                documentTemplateIds,
            }: DocumentCategoryPageTranslationPojo = data;

            if (success) {
                setTranslationData(data);
                setFormData({
                    documentCategoryPageTranslationId,
                    documentCategoryPageId,
                    url,
                    metaTitle,
                    metaKeyword,
                    metaDescription,
                    title,
                    heading,
                    status,
                    description,
                    documentTemplateIds: JSON.parse(
                        documentTemplateIds ?? '[]',
                    ),
                });

                setIsEditDocument(true);
            } else {
                notification.error(
                    message ?? Language.UNABLE_TO_FETCH_TRANSLATION_DATA,
                );
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setTranslationDataLoading(false);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === stringUtils.convertToCamelCase(Language.URL)) {
            newValue = newValue.toLowerCase();
        }

        setFormData({ ...formData, [name]: newValue });

        setErrors({ ...errors, [name]: undefined });
    };

    const validateForm = (): boolean => {
        let newErrors: FormErrors = {};
        const regexForCheckingCommaSeperatedString =
            /^([A-Za-zÀ-ÖØ-öø-ÿ0-9!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?`~\s]+?)(,\s*[A-Za-zÀ-ÖØ-öø-ÿ0-9!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?`~\s]+?)*$/;
        const regexForCheckingUrl = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9-]+$/;

        let isValid = true;

        // URL
        if (!formData.url?.trim()) {
            newErrors.url = Language.URL_REQUIRED;
            isValid = false;
        } else if (!regexForCheckingUrl.test(formData.url)) {
            newErrors.url = Language.URL_SHOULD_NOT_CONTAIN_WHITE_SPACE;
            isValid = false;
        }

        if (!formData.metaTitle?.trim()) {
            newErrors.metaTitle = Language.GOOGLE_TITLE_REQUIRED;
            isValid = false;
        }

        if (!formData.metaKeyword?.trim()) {
            newErrors.metaKeyword = Language.GOOGLE_KEYWORD_REQUIRED;
            isValid = false;
        } else {
            if (
                !regexForCheckingCommaSeperatedString.test(
                    formData.metaKeyword.trim(),
                )
            ) {
                newErrors.metaKeyword =
                    Language.KEYWORD_SHOULD_CONTAIN_COMMA_SEPERATED;
                isValid = false;
            }
        }

        if (!formData.metaDescription?.trim()) {
            newErrors.metaDescription = Language.GOOGLE_DESCRIPTION_REQUIRED;
            isValid = false;
        }

        if (!formData.title?.trim()) {
            newErrors.title = Language.TITLE_REQUIRED;
            isValid = false;
        }

        if (!formData.heading?.trim()) {
            newErrors.heading = Language.HEADING_REQUIRED;
            isValid = false;
        }

        if (!formData.description?.trim()) {
            newErrors.description = Language.DESCRIPTION_REQUIRED;
            isValid = false;
        }

        if (
            !formData.documentTemplateIds ||
            formData.documentTemplateIds.length === 0
        ) {
            newErrors.documentTemplateIds = Language.DOCUMENT_TEMPLATE_REQUIRED;
            isValid = false;
        }

        Object.keys(newErrors).forEach((key, value) => {
            if (
                typeof newErrors[key] === 'object' &&
                Object.keys(newErrors[key]).length === 0
            ) {
                delete newErrors[key];
            }
        });

        setErrors(newErrors);
        let errorFields = Object.keys(newErrors);
        if (errorFields.length > 0) {
            const firstErrorElement = document.getElementsByName(
                errorFields[0],
            )[0];
            if (firstErrorElement) {
                firstErrorElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                firstErrorElement.focus();
            }

            return false;
        }

        return isValid;
    };

    const saveTranslation = async () => {
        setShowLoader(true);
        try {
            let payload: any = {
                ...{
                    metaTitle: formData.metaTitle,
                    metaKeyword: formData.metaKeyword,
                    metaDescription: formData.metaDescription,
                    title: formData.title,
                    url: formData.url,
                    heading: formData.heading,
                    description: formData.description,
                    documentTemplateIds: formData.documentTemplateIds,
                },
                documentCategoryPageId: id,
                languageId: APP_LANGAUGE[language],
            };
            const { success, message } =
                await documentCategoryPageTranslationService.create(payload);
            if (success) {
                notification.success(message);
                getDocumentGroupPageTranslationData();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const updateTranslation = async () => {
        setShowLoader(true);
        try {
            let payload = {
                ...{
                    metaTitle: formData.metaTitle,
                    metaKeyword: formData.metaKeyword,
                    metaDescription: formData.metaDescription,
                    url: formData.url,
                    title: formData.title,
                    heading: formData.heading,
                    description: formData.description,
                    documentTemplateIds: formData.documentTemplateIds,
                },
                documentCategoryPageId: id,
                languageId: APP_LANGAUGE[language],
                documentCategoryPageTranslationId:
                    translationData?.documentCategoryPageTranslationId,
            };

            const { success, message } =
                await documentCategoryPageTranslationService.update(payload);
            if (success) {
                notification.success(message);
                getDocumentGroupPageTranslationData();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        if (!validateForm()) return;

        if (isEditDocument) {
            updateTranslation();
        } else {
            saveTranslation();
        }
    };

    const _checkForUrl = async () => {
        let existingUrl = translationData?.url;
        let newUrl = formData.url;

        if (existingUrl === newUrl || newUrl === '') {
            return true;
        } else {
            try {
                const { success, message } =
                    await documentCategoryPageTranslationService.isUrlExists(
                        newUrl,
                    );
                if (success) {
                    const newErrors: FormErrors = {};
                    newErrors.url = message;
                    setErrors(newErrors);
                }
            } catch (error) {
                notification.error(Language.SOMETHING_IS_WRONG);
                logger.error(error);
            }
        }
    };

    const changeDocumentTemplateTranslationStatus = async (status: number) => {
        try {
            if (translationData?.documentCategoryPageTranslationId) {
                let response =
                    await documentCategoryPageTranslationService.delete(
                        translationData?.documentCategoryPageTranslationId,
                        status,
                    );
                if (response) {
                    if (status === 1) {
                        notification.success(
                            Language.DOCUMENT_GROUP_PAGE_TRANSLATION_ACTIVATED_SUCCESSFULLY,
                        );
                    } else {
                        notification.success(
                            Language.DOCUMENT_GROUP_PAGE_TRANSLATION_INACTIVATED_SUCCESSFULLY,
                        );
                    }
                    getDocumentGroupPageTranslationData();
                } else {
                    notification.error(
                        Language.CANNOT_CHANGE_DOCUMENT_GROUP_PAGE_TRANSLATION_STATUS_ERROR,
                    );
                }
            } else {
                notification.error(Language.UNABLE_TO_FETCH_TRANSLATION_DATA);
            }
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(e);
        }
    };

    const _onKeyDown = (e: { key: string; preventDefault: () => void }) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const target = e.target as HTMLTextAreaElement;
        const { name, value } = target;
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        const clean = pasted.replace(/\r?\n|\r/g, ' ');
        let data = {
            ...formData,
            [name]: (formData[name] || '') + clean,
        };
        setFormData(data);
    };

    const openHintModal = (imageSource: string) => {
        setHintImage(imageSource);
        setFieldPreviewModalVisible(true);
    };

    const filteredDocumentTemplates = allActiveDocumentTemplates.filter(
        (template) =>
            template.name
                .toLowerCase()
                .includes(searchDocumentTemplateText.toLowerCase()),
    );

    return (
        <Layout>
            {!translationDataLoading && (
                <>
                    {showLoader && <Loader />}
                    <CCard className="mb-4">
                        <CCardHeader>
                            <strong>
                                {name} / {LANGAUGE_NAME[language]}
                            </strong>
                            <div className="d-flex align-items-center justify-content-between gap-3">
                                <div className="d-flex align-items-center gap-2">
                                    <CFormLabel
                                        htmlFor="statusToggle"
                                        className={`mb-0 ${formData.status === 0 ? 'text-dark' : 'text-muted'}`}
                                    >
                                        {Language.INACTIVE}
                                    </CFormLabel>
                                    <CFormSwitch
                                        id="statusToggle"
                                        name="status"
                                        className="mt-1"
                                        checked={formData.status === 1}
                                        onChange={(e) =>
                                            changeDocumentTemplateTranslationStatus(
                                                e.target.checked
                                                    ? DOCUMENT_CATEGORY_PAGE_TRANSLATION_STATUS_ACTIVE
                                                    : DOCUMENT_CATEGORY_PAGE_TRANSLATION_STATUS_INACTIVE,
                                            )
                                        }
                                        label=""
                                    />
                                    <CFormLabel
                                        htmlFor="statusToggle"
                                        className={`mb-0 ${formData.status === 1 ? 'text-dark' : 'text-muted'}`}
                                    >
                                        {Language.ACTIVE}
                                    </CFormLabel>
                                </div>
                                {isEdit &&
                                translationData?.url &&
                                translationData.url !== '' &&
                                translationData.status === 1 ? (
                                    <CButton
                                        onClick={() =>
                                            window.open(
                                                WEBSITE_BASE_URL +
                                                    '/' +
                                                    translationData.url,
                                                '_blank',
                                            )
                                        }
                                        color="primary"
                                    >
                                        {Language.PREVIEW}
                                    </CButton>
                                ) : null}
                            </div>
                        </CCardHeader>
                        <CCardBody>
                            <CForm>
                                {/* URL */}
                                <CFormLabel
                                    htmlFor={stringUtils.convertToCamelCase(
                                        Language.URL,
                                    )}
                                >
                                    {Language.URL}
                                </CFormLabel>
                                <CFormInput
                                    type="text"
                                    placeholder={`Ex. ${name?.toLowerCase().split(' ').join('-')}`}
                                    value={formData.url}
                                    invalid={!!errors.url}
                                    feedback={errors.url}
                                    onChange={(e) => handleChange(e)}
                                    onBlur={_checkForUrl}
                                    name={stringUtils.convertToCamelCase(
                                        Language.URL,
                                    )}
                                    maxLength={200}
                                />
                                <LengthChecker
                                    currentLength={formData.url.length}
                                    maxLength={200}
                                />

                                <SeoFields
                                    name={name}
                                    stringUtils={stringUtils}
                                    data={{
                                        url: formData?.url,
                                        metaTitle: formData?.metaTitle,
                                        metaKeyword: formData?.metaKeyword,
                                        metaDescription:
                                            formData?.metaDescription,
                                    }}
                                    errors={{
                                        url: errors?.url,
                                        metaTitle: errors?.metaTitle,
                                        metaKeyword: errors?.metaKeyword,
                                        metaDescription:
                                            errors?.metaDescription,
                                    }}
                                    onBlurUrlField={_checkForUrl}
                                    handleChange={handleChange}
                                    handlePaste={handlePaste}
                                    onKeyDown={_onKeyDown}
                                    metaTitleHint={
                                        Language.CATEGORY_PAGE_META_TITLE_HINT
                                    }
                                    metaKeywordHint={
                                        Language.CATEGORY_PAGE_META_KEYWORD_HINT
                                    }
                                    metaDescriptionHint={
                                        Language.CATEGORY_PAGE_META_DESCRIPTION_HINT
                                    }
                                />

                                {/* Category Title */}
                                <FieldHeadingWithHintText
                                    stringUtils={stringUtils}
                                    heading={Language.TITLE}
                                    openModal={() => {
                                        openHintModal(
                                            DocumentCategoryTranslationTitleHint,
                                        );
                                    }}
                                />
                                <CFormInput
                                    value={formData?.title}
                                    invalid={!!errors?.title}
                                    feedback={errors?.title}
                                    className="mt-2"
                                    type="text"
                                    name={stringUtils.convertToCamelCase(
                                        Language.TITLE,
                                    )}
                                    placeholder={Language.TITLE}
                                    onChange={handleChange}
                                    maxLength={100}
                                />
                                <LengthChecker
                                    currentLength={formData?.title?.length ?? 0}
                                    maxLength={100}
                                />

                                {/* Category Heading */}
                                <FieldHeadingWithHintText
                                    stringUtils={stringUtils}
                                    heading={Language.HEADING}
                                    openModal={() => {
                                        openHintModal(
                                            DocumentCategoryTranslationHeadingHint,
                                        );
                                    }}
                                />
                                <CFormInput
                                    value={formData?.heading}
                                    invalid={!!errors?.heading}
                                    feedback={errors?.heading}
                                    className="mt-2"
                                    type="text"
                                    name={stringUtils.convertToCamelCase(
                                        Language.HEADING,
                                    )}
                                    placeholder={Language.HEADING}
                                    onChange={handleChange}
                                    maxLength={100}
                                />
                                <LengthChecker
                                    currentLength={
                                        formData?.heading?.length ?? 0
                                    }
                                    maxLength={100}
                                />

                                {/* Category Description */}
                                <FieldHeadingWithHintText
                                    stringUtils={stringUtils}
                                    heading={Language.DESCRIPTION}
                                    openModal={() => {
                                        openHintModal(
                                            DocumentCategoryTranslationDescriptionHint,
                                        );
                                    }}
                                />
                                <HintText marginBottom="10px">
                                    {
                                        Language.DOCUMENT_CATEGORY_DESCRIPTION_HINT
                                    }
                                </HintText>
                                <CFormTextarea
                                    placeholder={Language.DESCRIPTION}
                                    onKeyDown={_onKeyDown}
                                    onChange={handleChange}
                                    onPaste={handlePaste}
                                    value={formData?.description}
                                    invalid={!!errors?.description}
                                    feedback={errors?.description}
                                    maxLength={300}
                                    style={{ height: 180 }}
                                    name={stringUtils.convertToCamelCase(
                                        Language.DESCRIPTION,
                                    )}
                                />
                                <LengthChecker
                                    currentLength={
                                        formData?.description?.length ?? 0
                                    }
                                    maxLength={300}
                                />

                                <CFormLabel
                                    htmlFor={stringUtils.convertToCamelCase(
                                        Language.DOCUMENT_TEMPLATES,
                                    )}
                                >
                                    {Language.DOCUMENT_TEMPLATES}
                                </CFormLabel>

                                <CFormInput
                                    placeholder={Language.SEARCH_TEMPLATES}
                                    value={searchDocumentTemplateText}
                                    onChange={(e) =>
                                        setSearchDocumentTemplateText(
                                            e.target.value,
                                        )
                                    }
                                />

                                {filteredDocumentTemplates.map((template) => (
                                    <div key={template.documentTemplateId}>
                                        <CFormCheck
                                            type="checkbox"
                                            id={`documentTemplate-${template.documentTemplateId}`}
                                            name="documentTemplateIds"
                                            className="mt-3 border-black-checkbox rounded-1"
                                            checked={formData.documentTemplateIds?.includes(
                                                template.documentTemplateId,
                                            )}
                                            onChange={(e) => {
                                                const id = Number(
                                                    template.documentTemplateId,
                                                );
                                                let newDocumentTemplateIds =
                                                    formData.documentTemplateIds
                                                        ? [
                                                              ...formData.documentTemplateIds,
                                                          ]
                                                        : [];

                                                if (e.target.checked) {
                                                    newDocumentTemplateIds.push(
                                                        id,
                                                    );
                                                } else {
                                                    newDocumentTemplateIds =
                                                        newDocumentTemplateIds.filter(
                                                            (x) => x !== id,
                                                        );
                                                }

                                                setFormData({
                                                    ...formData,
                                                    documentTemplateIds:
                                                        newDocumentTemplateIds,
                                                });
                                            }}
                                            label={
                                                <span
                                                    style={{
                                                        fontWeight: 400,
                                                        color: 'inherit',
                                                    }}
                                                >
                                                    {template.name}
                                                </span>
                                            }
                                        />
                                    </div>
                                ))}

                                {errors?.documentTemplateIds && (
                                    <div className="mt-2 alert alert-danger">
                                        {errors?.documentTemplateIds}
                                    </div>
                                )}

                                <div className="d-flex justify-content-end">
                                    <CButton
                                        color="success"
                                        type="submit"
                                        className="mt-4"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleSubmit(e as any);
                                        }}
                                    >
                                        {isEditDocument
                                            ? Language.UPDATE_DOCUMENT_GROUP_PAGE
                                            : Language.SAVE_DOCUMENT_GROUP_PAGE}
                                    </CButton>
                                </div>
                            </CForm>
                        </CCardBody>
                    </CCard>
                </>
            )}

            <FieldPreviewModal
                visible={fieldPreviewModalVisible}
                onClose={() => setFieldPreviewModalVisible(false)}
                imageSrc={hintImage}
            />
        </Layout>
    );
};

export default DocumentCategoryTranslation;
